// askSmart-ui/src/pages/TOSP/portal/tenantSalesApi.ts
import { TenantSalesMonth } from "./tenantSales";

// The sales endpoints.
//
// Kept apart from tospApi's generic collection helpers because sales don't
// follow that shape: submission is a POST a tenant may make with only `.view`,
// correction is an admin-only PUT, and the list a caller gets back depends on
// who they are. The rules live on the server; this is the thin wrapper.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export class SalesApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "SalesApiError";
    this.status = status;
  }
}

async function call(path: string, init?: RequestInit): Promise<any> {
  const res = await fetch(`${BACKEND}/tosp${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new SalesApiError(body?.error || `Request failed (${res.status})`, res.status);
  }
  return body;
}

/** Every month the caller is entitled to see — their own, or all of them. */
export async function listSales(): Promise<TenantSalesMonth[]> {
  const body = await call("/tenant-sales");
  return Array.isArray(body.sales) ? body.sales : [];
}

/**
 * Reports one month. The server refuses a month already reported, with a 409 —
 * final-once-submitted is enforced there, not here, because a rule the browser
 * keeps is a rule anyone with devtools can break.
 */
export async function submitSales(
  tenantId: string,
  year: number,
  month: number,
  grossSales: number,
): Promise<TenantSalesMonth> {
  const body = await call("/tenant-sales", {
    method: "POST",
    body: JSON.stringify({ tenantId, year, month, grossSales }),
  });
  return body.sale;
}

/** Corrects a reported month. Admin only; the original is kept server-side. */
export async function amendSales(
  id: string,
  grossSales: number,
): Promise<TenantSalesMonth> {
  const body = await call(`/tenant-sales/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify({ grossSales }),
  });
  return body.sale;
}
